import { useEffect, useState } from "react";
import popupStyles from "../../css/custom-popup.module.css"
import PropTypes from "prop-types";
import Slider from "react-slick";

const ZOOM = 2.5;

/**
 * Product photo with click/tap-to-zoom.
 * Replaces react-image-magnify, whose hover lens had a fixed 800x1200 box: on the square product
 * photos it showed a white box overflowing the popup, and it never worked on touch screens.
 * Click/tap zooms in around that point, moving the mouse/finger pans, click/tap again zooms out.
 */
const ZoomableImage = ({ src, alt, zoomed, onToggle }) => {
    const [origin, setOrigin] = useState("50% 50%");

    const pointToOrigin = (clientX, clientY, el) => {
      const rect = el.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
      setOrigin(`${x}% ${y}%`);
    };

    const onClick = (e) => {
      pointToOrigin(e.clientX, e.clientY, e.currentTarget);
      onToggle();
    };

    const onMouseMove = (e) => {
      if (zoomed) pointToOrigin(e.clientX, e.clientY, e.currentTarget);
    };

    const onTouchMove = (e) => {
      if (zoomed && e.touches.length === 1) {
        pointToOrigin(e.touches[0].clientX, e.touches[0].clientY, e.currentTarget);
      }
    };

    return (
      <div
        className="zoomable-image"
        onClick={onClick}
        onMouseMove={onMouseMove}
        onTouchMove={onTouchMove}
        style={{
          width: "100%",
          height: "250px",
          overflow: "hidden",
          cursor: zoomed ? "zoom-out" : "zoom-in",
          // while zoomed, a finger pans the photo instead of scrolling the page or swiping the slider;
          // zoomed out, the browser's own two-finger pinch zoom still works
          touchAction: zoomed ? "none" : "pan-y pinch-zoom",
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transform: zoomed ? `scale(${ZOOM})` : "none",
            transformOrigin: origin,
            transition: "transform 0.2s ease-out",
            userSelect: "none",
          }}
        />
      </div>
    );
};

const ProductImage = (props) => {
    const [show, setShow] = useState(false);
    const [zoomed, setZoomed] = useState(false);

    const closeHandler = (e) => {
      if(e.target.className === popupStyles.overlay || e.target.className === popupStyles.close){
        setShow(false);
        setZoomed(false);
        props.onClose();
      }
    };

    useEffect(() => {
      setShow(props.show);
    }, [props.show]);

    const validImages = props.images.filter((img) => img && img.trim() !== '');

    // a new product opened in the popup always starts zoomed out
    const imagesKey = validImages.join('|');
    useEffect(() => {
      setZoomed(false);
    }, [imagesKey]);

    const settings = {
        dots: true,
        infinite: false,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        // while zoomed, dragging pans the photo; swiping to the back side only when zoomed out
        swipe: !zoomed,
        draggable: !zoomed,
        beforeChange: () => setZoomed(false),
      };

    const toggleZoom = () => setZoomed((z) => !z);

  return (
    <div id="product-image-popup"
      style={{
        visibility: show ? "visible" : "hidden",
        opacity: show ? "1" : "0"
      }}
      className={popupStyles.overlay}
      onClick={closeHandler}
    >
        <span className={popupStyles.close} onClick={closeHandler}>
          &times;
        </span>
        <div className={[popupStyles.popup + ' image_popup']}>
        <h2>{props.title}</h2>
        {validImages.length > 1 ?
        <Slider {...settings}>
        {validImages.map((image) =>
            <div key={image}>
              <ZoomableImage
                src={process.env.PUBLIC_URL + `/images` + image}
                alt={props.title || ""}
                zoomed={zoomed}
                onToggle={toggleZoom}
              />
            </div>
        )}
        </Slider>
        :
        <ZoomableImage
          src={process.env.PUBLIC_URL + `/images` + validImages[0]}
          alt={props.title || ""}
          zoomed={zoomed}
          onToggle={toggleZoom}
        />
      }
      </div>
    </div>
  );
};

ProductImage.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
  };

export default ProductImage;
